import React, { SyntheticEvent, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { callDeleteUser } from "../../api/UserActions";
import { generateResetLink } from "../../api/EmailApi";
import { ApiStatus } from "../../api/ApiUtilities";
import { toast } from "../../common/notify";
import ContainerCard from "../../common/CardContainer";
import EmailNotice from "../../common/EmailNotice";
import CheckboxInput from "../../common/CheckboxInput";
import MultiSelectInput, { MultiSelectOption } from "../../common/MultiSelectInput";
import SelectInput, { SelectOption } from "../../common/SelectInput";
import TextInput from "../../common/TextInput";
import { User } from "../../data/User";
import { UserRoles } from "../../state/state";

export interface UserEditProps {
  user: User;
  onSave: (username: string, preferred: string, firstname: string, lastname: string, phone: string, house: string, parish: string, userRoles: string, enabled: boolean) => Promise<string>;
  onCancel: () => void;
  onDelete: () => Promise<string>;
}

function UserEdit({ user, onSave, onCancel, onDelete }: UserEditProps) {
  const [enabled, setEnabled] = useState(user?.enabled);
  const [preferred, setPreferredName] = useState(user?.preferredName);
  const [firstname, setFirstname] = useState(user.firstname);
  const [lastname, setLastname] = useState(user.lastname);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.mobilePhone);
  const [house, setHouse] = useState(user.house);
  const [parish, setParish] = useState(user?.parish);
  const [userRoles, setUserRoles] = useState(user.userRoles);
  const [errors, setErrors] = useState({} as Errors);
  const [saving, setSaving] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [showResetLink, setShowResetLink] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  interface Errors {
    enabled?: string;
    preferred?: string;
    firstname?: string;
    lastname?: string;
    email?: string;
    house?: string;
    parish?: string;
    userRoles?: string;
    phone?: string;
    onSave?: string;
  }

  function userHouseList(): SelectOption[] {
    return [
      { value: "", text: "" },
      { value: "Lay", text: "Lay" },
      { value: "Clergy", text: "Clergy" },
      { value: "Bishops", text: "Bishops" },
    ];
  }
  function userRolesList(): MultiSelectOption[] {
    let list = [
      { value: UserRoles.Participant, text: UserRoles.Participant },
      { value: UserRoles.Organiser, text: UserRoles.Organiser },
      { value: UserRoles.Admin, text: UserRoles.Admin },
    ];
    // TODO: check logged in users role here and only add these 2 if a super admin
    list.push({ value: UserRoles.Presenter, text: UserRoles.Presenter });
    list.push({ value: UserRoles.SuperAdmin, text: UserRoles.SuperAdmin });
    return list;
  }

  function cancel(event: SyntheticEvent) {
    setSaving(true);
    event.preventDefault();
    onCancel();
  }

  function deleteUser(event: SyntheticEvent) {
    setSaving(true);
    event?.preventDefault();
    onDelete()
      .then((msg) => {
        if (msg) {
          setErrors({ onSave: msg });
        }
      })
      .finally(() => {
        setSaving(false);
      });
  }

  // mirrors the server rule: a new user, or a changed email address
  // (which re-keys the account), triggers the activation email on save
  const isNewUser = !user.userId;
  const emailChanged = !isNewUser && (email ?? "").trim().toLowerCase() !== (user.email ?? "").trim().toLowerCase();

  function generatePasswordReset(event: SyntheticEvent) {
    event.preventDefault();
    setGeneratingLink(true);
    setLinkCopied(false);
    generateResetLink(user.username)
      .then((response) => {
        if (response.status === ApiStatus.Ok && response.data) {
          setResetLink(response.data);
          setShowResetLink(true);
        } else {
          toast.error(response.message || "Could not generate a reset link");
        }
      })
      .catch((e: Error) => toast.error(`Could not generate a reset link: ${e.message}`))
      .finally(() => setGeneratingLink(false));
  }

  function copyResetLink() {
    navigator.clipboard
      ?.writeText(resetLink)
      .then(() => setLinkCopied(true))
      .catch(() => setLinkCopied(false));
  }

  function save(event: SyntheticEvent) {
    setSaving(true);
    // TODO: add client side validation
    setErrors({});
    event.preventDefault();
    onSave(email, preferred, firstname, lastname, phone, house, parish, userRoles, enabled)
      .then((msg) => {
        setErrors({ onSave: msg });
      })
      .catch((e: Error) => {
        setErrors({ onSave: e.message });
      })
      .finally(() => {
        setSaving(false);
      });
  }

  return (
    <ContainerCard heading={user.userId ? "Edit User" : "New User"}>
      <form style={{"margin":"auto","width" : "96%"}} onSubmit={save}>
        <div className="row mb-2">
          <div className="col">Username</div>
          <div className="col">{user.username}</div>
        </div>
        <div className="row mb-2">
          <div className="col">Id (properties)</div>
          <div className="col">
            {user.alternateId}&nbsp;&nbsp;({user.property1}&nbsp;{user.property2}&nbsp;{user.property3}&nbsp;{user.property4})
          </div>
        </div>
        <br></br>
        <div className="row">
          <CheckboxInput name="Enabled" label="User Enabled" checked={enabled} error={errors.enabled} onChange={setEnabled} />
        </div>
        <div className="row">
          <TextInput name="Prefered" label="Preferred First Name" type="text" placeholder="" value={preferred} error={errors.preferred} onChange={setPreferredName} />
        </div>
        <div className="row">
          <TextInput name="Firstname" label="First Name" type="text" placeholder="" value={firstname} error={errors.firstname} onChange={setFirstname} />
        </div>
        <div className="row">
          <TextInput name="Lastname" label="Last Name" type="text" placeholder="" value={lastname} error={errors.lastname} onChange={setLastname} />
        </div>
        <div className="row">
          <TextInput name="Email" label="Email" type="email" placeholder="" value={email} error={errors.email} onChange={setEmail} />
        </div>
        <div className="row">
          <TextInput name="Phone" label="Mobile Phone number" type="phone" placeholder="" value={phone} error={errors.phone} onChange={setPhone} />
        </div>
        <div className="row">
          <SelectInput name="House" label="House" value={house} defaultOption="" error={errors.house} onChange={setHouse} options={userHouseList()} />
        </div>
        <div className="row">
          <TextInput name="Parish" label="Parish" type="text" placeholder="" value={parish} error={errors.parish} onChange={setParish} />
        </div>
        <div className="row">
          <MultiSelectInput name="UserRoles" label="User Roles" value={userRoles} error={errors.userRoles} onChange={setUserRoles} options={userRolesList()} />
        </div>
        {errors.onSave && (
          <div className="row mt-2 mx-1">
            <div className="alert alert-danger">{errors.onSave}</div>
          </div>
        )}
        <div className="d-flex flex-wrap my-2 gap-2">
          <div className="px-2 me-auto">
            {user.userId ? (
              <>
                <button type="button" disabled={generatingLink} className="btn btn-light" onClick={generatePasswordReset} title="Emails the user a password reset link and shows it to you">
                  {generatingLink ? "Generating..." : "Generate password reset"}
                </button>
                <EmailNotice>
                  Sends the user the <strong>Password reset</strong> email and shows you the link.
                </EmailNotice>
              </>
            ) : null}
          </div>
          <div className="px-2 ms-auto">
            <button type="button" disabled={saving || !user.userId} className="btn btn-danger me-5" onClick={deleteUser}>
              Delete User
            </button>
            <button type="button" disabled={saving} className="btn btn-secondary mx-2" onClick={cancel}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn btn-primary mx-2">
              {saving ? "Saving..." : "Save"}
            </button>
            {isNewUser ? (
              <EmailNotice>
                Saving will email this user the <strong>New user activation</strong> email, with a link to set their password. Templates can be edited under Admin → Emails.
              </EmailNotice>
            ) : emailChanged ? (
              <EmailNotice>
                The email address has changed — saving will send the <strong>New user activation</strong> email to the new address, and the user will log in with it from now on.
              </EmailNotice>
            ) : (
              <p className="sl-no-email-note">Saving will not send any email.</p>
            )}
          </div>
        </div>
      </form>
      <Modal show={showResetLink} onHide={() => setShowResetLink(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Password reset link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            An email with this link has been sent to <strong>{user.email}</strong>. You can also copy it and pass it to them directly, or open it with them now if they're with you.
          </p>
          <div className="input-group">
            <input type="text" className="form-control font-monospace" readOnly value={resetLink} onFocus={(e) => e.target.select()} aria-label="Password reset link" />
            <button type="button" className="btn btn-primary" onClick={copyResetLink}>
              {linkCopied ? "Copied ✓" : "Copy"}
            </button>
          </div>
          <p className="text-muted small mt-2 mb-0">The link lets anyone set this user's password — only share it with the user themselves.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowResetLink(false)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </ContainerCard>
  );
}

export default UserEdit;
